/**
 * ELF-CIRCLE — Launch Markets & The Global Ambition (design artboard `#8b`).
 *
 * An orbit diagram: the home-base metro sits at the centre, active-expansion
 * markets ride the inner ring and roadmap markets the outer one, both drifting
 * continuously. Hovering a market fires a "heartbeat" pulse down the connector
 * and the detail box only updates once the pulse has visually arrived (~80ms),
 * so the diagram reads as one wired circuit rather than two separate widgets.
 *
 * Static page — imagery is bundled rather than an editable CMS slot.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import ElfFooter from '../ElfFooter';
import ElfPageNav, { ElfCrumb } from '../ElfPageNav';
import ElfShell from '../ElfShell';
import { useConsult } from '../elfConsult';
import { route } from '../../../../../shared/utils';
import atlantaArt from '../../../../../assets/elf/market-atlanta.jpg';
import chicagoArt from '../../../../../assets/elf/market-chicago.jpg';
import dallasArt from '../../../../../assets/elf/market-dallas.jpg';
import dcArt from '../../../../../assets/elf/market-dc.jpg';
import houstonArt from '../../../../../assets/elf/market-houston.jpg';
import laArt from '../../../../../assets/elf/market-la.jpg';
import miamiArt from '../../../../../assets/elf/market-miami.jpg';
import nynjArt from '../../../../../assets/elf/market-nynj.jpg';
import philadelphiaArt from '../../../../../assets/elf/market-philadelphia.jpg';
import phoenixArt from '../../../../../assets/elf/market-phoenix.jpg';

type Tier = 'Home base' | 'Active expansion' | 'Roadmap';

interface Market {
  id: string;
  /** Short label drawn on the orbiting node. */
  label: string;
  /** Full metro name, shown in the detail box. */
  name: string;
  pop: string;
  tier: Tier;
  note: string;
  /** Starting angle on the ring, in degrees. */
  ang: number;
  /** Ring radius in px. */
  rad: number;
  /** Drift rate in deg/sec; negative counter-rotates the roadmap ring. */
  speed: number;
}

const HOME: Market = {
  id: 'nynj',
  label: 'NY–NJ',
  name: 'New York–Newark–Jersey City',
  pop: '19.6M',
  tier: 'Home base',
  note: 'Lincoln Center, Tribeca, off-Broadway — proven.',
  ang: 0,
  rad: 0,
  speed: 0,
};

const ORBIT: Market[] = [
  { id: 'atl', label: 'Atlanta', name: 'Atlanta', pop: '6.5M', tier: 'Active expansion', note: 'Music & entertainment hub.', ang: 20, rad: 130, speed: 4 },
  { id: 'dc', label: 'D.C.', name: 'Washington, D.C.', pop: '6.5M', tier: 'Active expansion', note: 'Corridor proximity via Made In New Jersey.', ang: 110, rad: 130, speed: 4 },
  { id: 'mia', label: 'Miami', name: 'Miami', pop: '6.4M', tier: 'Active expansion', note: 'Fashion & entertainment hub.', ang: 200, rad: 130, speed: 4 },
  { id: 'phl', label: 'Philadelphia', name: 'Philadelphia', pop: '6.3M', tier: 'Active expansion', note: '21-county Made In New Jersey footprint.', ang: 290, rad: 130, speed: 4 },
  { id: 'la', label: 'Los Angeles', name: 'Los Angeles', pop: '13.2M', tier: 'Roadmap', note: 'Largest roadmap market.', ang: 0, rad: 230, speed: -2.4 },
  { id: 'chi', label: 'Chicago', name: 'Chicago', pop: '9.8M', tier: 'Roadmap', note: 'Roadmap market.', ang: 72, rad: 230, speed: -2.4 },
  { id: 'dfw', label: 'Dallas–FW', name: 'Dallas–Fort Worth', pop: '7.9M', tier: 'Roadmap', note: 'Roadmap market.', ang: 144, rad: 230, speed: -2.4 },
  { id: 'hou', label: 'Houston', name: 'Houston', pop: '7.9M', tier: 'Roadmap', note: 'Roadmap market.', ang: 216, rad: 230, speed: -2.4 },
  { id: 'phx', label: 'Phoenix', name: 'Phoenix', pop: '5.4M', tier: 'Roadmap', note: 'Roadmap market.', ang: 288, rad: 230, speed: -2.4 },
];

/**
 * The picture behind the diagram for each market, keyed by market id — it
 * follows whichever market the detail box is showing. A market with no entry
 * leaves the plain panel.
 */
const MARKET_ART: Record<string, string> = {
  nynj: nynjArt,
  atl: atlantaArt,
  dc: dcArt,
  mia: miamiArt,
  phl: philadelphiaArt,
  la: laArt,
  chi: chicagoArt,
  dfw: dallasArt,
  hou: houstonArt,
  phx: phoenixArt,
};

/** How far the commitment bar fills for each tier. */
const TIER_FILL: Record<Tier, string> = {
  'Home base': '100%',
  'Active expansion': '62%',
  Roadmap: '30%',
};

/** The heartbeat's travel time — the box waits this long before it updates. */
const TRAVEL_MS = 80;
/** Idle time before the box falls back to the home-base default. */
const REVERT_MS = 2000;
/**
 * Touch screens: the pulse is slowed so a tap visibly sends it down the wire
 * (80ms is invisible without a pointer resting on the node), and the box still
 * waits for it to arrive. Matches the `(hover: none)` animation-duration in CSS.
 */
const TOUCH_TRAVEL_MS = 520;
/** Touch screens: how often the tour moves to the next market. */
const TOUR_MS = 3200;
/** Touch screens: how long a tap holds the tour off. */
const TOUR_HOLD_MS = 8000;

/** Polar to cartesian, in px from the orbit's centre. */
const place = (angDeg: number, rad: number) => {
  const a = (angDeg * Math.PI) / 180;
  return { x: `${(Math.cos(a) * rad).toFixed(2)}px`, y: `${(Math.sin(a) * rad).toFixed(2)}px` };
};
const isTouch = () => window.matchMedia?.('(hover: none)').matches ?? false;

/** CSS custom properties aren't in React's CSSProperties, so widen the type. */
const vars = (v: Record<string, string>) => v as React.CSSProperties;

export const LaunchMarkets: React.FC = () => {
  const consult = useConsult();
  const orbitRef = useRef<HTMLDivElement>(null);
  /** Read by the rAF loop; a ref so hovering never re-renders the orbit. */
  const pausedRef = useRef(false);
  const beatRef = useRef<number>();
  const homeRef = useRef<number>();

  const [detail, setDetail] = useState<Market>(HOME);
  const [arrived, setArrived] = useState(false);
  const [live, setLive] = useState(false);
  const [pulsing, setPulsing] = useState(false);
  /** Market showing the hover/tap highlight, so touch gets the hover look. */
  const [activeId, setActiveId] = useState<string | null>(null);
  /** When the visitor last tapped — holds the touch tour off. */
  const lastTapRef = useRef(0);

  /* --- orbit drift --------------------------------------------------------- */
  useEffect(() => {
    const orbit = orbitRef.current;
    if (!orbit) return;
    // Reduced motion slows the ring rather than stopping it — a frozen diagram
    // reads as broken, which is what stopping it looked like on phones.
    const gentle = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;

    const nodes = Array.from(orbit.querySelectorAll<HTMLElement>('.orbnode')).map((el) => ({
      el,
      ang: Number(el.dataset.ang ?? 0),
      speed: Number(el.dataset.speed ?? 0),
      rad: Number(el.dataset.rad ?? 0),
    }));

    // On a phone the orbit is drawn at ~0.6 scale, so the same degrees per
    // second cross far fewer pixels and the ring reads as frozen. Dividing by
    // the drawn scale keeps the laptop's on-screen speed. A transform doesn't
    // resize the box, so this listens to the window rather than the element.
    let boost = 1;
    const measure = () => {
      const scale = orbit.getBoundingClientRect().width / orbit.offsetWidth;
      boost = scale > 0 ? 1 / scale : 1;
    };
    measure();
    window.addEventListener('resize', measure);

    let raf = 0;
    let last = performance.now();
    const tick = (now: number) => {
      // Clamped so returning to a backgrounded tab doesn't jump the ring.
      const dt = Math.min((now - last) / 1000, 0.1);
      last = now;
      if (!pausedRef.current) {
        for (const n of nodes) {
          n.ang = (n.ang + n.speed * boost * dt * (gentle ? 0.45 : 1)) % 360;
          // Positions are computed here, not with CSS cos()/sin(), which older
          // phone browsers don't support — there the ring could not move at all.
          const { x, y } = place(n.ang, n.rad);
          n.el.style.setProperty('--x', x);
          n.el.style.setProperty('--y', y);
        }
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', measure);
    };
  }, []);

  useEffect(
    () => () => {
      window.clearTimeout(beatRef.current);
      window.clearTimeout(homeRef.current);
    },
    [],
  );

  /** Swap the box contents and replay its pop animation. */
  const fill = useCallback((m: Market) => {
    setDetail(m);
    setPulsing(false);
    requestAnimationFrame(() => setPulsing(true));
  }, []);

  const enter = useCallback(
    (m: Market) => {
      pausedRef.current = true;
      setActiveId(m.id);
      window.clearTimeout(beatRef.current);
      window.clearTimeout(homeRef.current);
      setArrived(false);
      // Drop and re-add `live` so the pulse animation restarts from the top.
      setLive(false);
      requestAnimationFrame(() => setLive(true));
      beatRef.current = window.setTimeout(() => {
        fill(m);
        setArrived(true);
      }, isTouch() ? TOUCH_TRAVEL_MS : TRAVEL_MS);
    },
    [fill],
  );

  const leave = useCallback(() => {
    pausedRef.current = false;
    setActiveId(null);
    window.clearTimeout(beatRef.current);
    window.clearTimeout(homeRef.current);
    setLive(false);
    setArrived(false);
    homeRef.current = window.setTimeout(() => fill(HOME), REVERT_MS);
  }, [fill]);

  /**
   * Touch and keyboard have no "leave": show the market and keep it shown until
   * another is chosen, and let the orbit keep drifting — hover pauses it only so
   * a node doesn't slide out from under a mouse pointer.
   */
  const show = useCallback(
    (m: Market) => {
      enter(m);
      pausedRef.current = false;
    },
    [enter],
  );
  const tap = useCallback(
    (m: Market) => {
      lastTapRef.current = Date.now();
      show(m);
    },
    [show],
  );

  /*
   * Touch tour. Hovering is how a laptop visitor sets the heartbeat going; a
   * phone has no hover, so there the pulse walks the markets on its own while
   * the orbit is on screen. A tap holds it off for a while, and reduced-motion
   * visitors don't get it.
   */
  useEffect(() => {
    const orbit = orbitRef.current;
    if (!orbit || !isTouch()) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const tour = [HOME, ...ORBIT];
    let i = 0;
    let visible = false;
    const io = new IntersectionObserver(([entry]) => {
      visible = entry.isIntersecting;
    }, { threshold: 0.35 });
    io.observe(orbit);
    const id = window.setInterval(() => {
      if (!visible || document.hidden || Date.now() - lastTapRef.current < TOUR_HOLD_MS) return;
      i = (i + 1) % tour.length;
      show(tour[i]);
    }, TOUR_MS);
    return () => {
      window.clearInterval(id);
      io.disconnect();
    };
  }, [show]);

  return (
    <ElfShell>
      <div className="elf-page">
        <div className="elf-page__panel">
          <ElfPageNav current="/launch-markets" />
          <div className="elf-page__bleed mktstage">
            {/* Every picture is mounted and only the current one is opaque, so
                moving between markets cross-fades instead of loading. */}
            {Object.entries(MARKET_ART).map(([id, art]) => (
              <img
                key={id}
                className="mktstage__art"
                src={art}
                alt=""
                data-on={detail.id === id ? 'true' : undefined}
              />
            ))}
            <span className="mktstage__shade" />
            <div className="elf-page__col mktstage__col">
              <div style={{ padding: '18px var(--pgut) 0' }}>
                <ElfCrumb trail={[{ label: 'Home', to: '/' }, 'Launch Markets']} />
              </div>

              <div style={{ padding: '34px var(--pgut) 4px', display: 'flex', flexDirection: 'column', gap: 14 }}>
                <span className="eyebrow" style={{ alignSelf: 'flex-start' }}>
                  <i />
                  Three tiers, sorted honestly
                </span>
                <h1 className="elf-page__h1">Proven, expanding, ahead.</h1>
              </div>

              <div className="mktgrid">
                <div className="mktorbit" ref={orbitRef}>
                  <div className="orbring r1" />
                  <div className="orbring r2" />
                  <div className="orbring r3" />

                  <div
                    className="orbcenter"
                    data-active={activeId === HOME.id ? 'true' : undefined}
                    role="button"
                    tabIndex={0}
                    onPointerEnter={(e) => {
                      if (e.pointerType === 'mouse') enter(HOME);
                    }}
                    onPointerLeave={(e) => {
                      if (e.pointerType === 'mouse') leave();
                    }}
                    onClick={() => tap(HOME)}
                    onKeyDown={(e) => {
                      if (e.key !== 'Enter' && e.key !== ' ') return;
                      e.preventDefault();
                      tap(HOME);
                    }}
                  >
                    {HOME.label}
                    <span className="orbstat">
                      {HOME.pop} · {HOME.tier}
                    </span>
                  </div>

                  {ORBIT.map((m) => (
                    <div
                      key={m.id}
                      className={m.tier === 'Roadmap' ? 'orbnode road' : 'orbnode'}
                      data-ang={m.ang}
                      data-speed={m.speed}
                      data-rad={m.rad}
                      data-active={activeId === m.id ? 'true' : undefined}
                      style={vars({ '--x': place(m.ang, m.rad).x, '--y': place(m.ang, m.rad).y })}
                      role="button"
                      tabIndex={0}
                      onPointerEnter={(e) => {
                        if (e.pointerType === 'mouse') enter(m);
                      }}
                      onPointerLeave={(e) => {
                        if (e.pointerType === 'mouse') leave();
                      }}
                      onClick={() => tap(m)}
                      onKeyDown={(e) => {
                        if (e.key !== 'Enter' && e.key !== ' ') return;
                        e.preventDefault();
                        tap(m);
                      }}
                    >
                      {m.label}
                      <span className="orbdetail">
                        {m.pop} · {m.tier === 'Roadmap' ? 'Roadmap' : 'Expansion'}
                      </span>
                    </div>
                  ))}
                </div>

                <div className={`orbconn${live ? ' live' : ''}${arrived ? ' arrived' : ''}`}>
                  <svg className="ecgsvg" viewBox="0 0 170 24" preserveAspectRatio="none" aria-hidden="true">
                    <path
                      className="ecgbase"
                      d="M0,12 L18,12 L24,4 L30,20 L36,8 L42,12 L64,12 L70,4 L76,20 L82,8 L88,12 L110,12 L116,4 L122,20 L128,8 L134,12 L170,12"
                    />
                    <path
                      className="ecgpulse"
                      d="M0,12 L18,12 L24,4 L30,20 L36,8 L42,12 L64,12 L70,4 L76,20 L82,8 L88,12 L110,12 L116,4 L122,20 L128,8 L134,12 L170,12"
                    />
                  </svg>
                </div>

                <div
                  className={`orbbox${arrived ? ' arrived' : ''}${pulsing ? ' pulse' : ''}`}
                  onAnimationEnd={(e) => {
                    if (e.target === e.currentTarget) setPulsing(false);
                  }}
                  aria-live="polite"
                >
                  <span className="orbboxtag">{detail.tier}</span>
                  <span className="orbboxname">{detail.name}</span>
                  <span className="orbboxpop">{detail.pop} metro population</span>
                  <p className="orbboxnote">{detail.note}</p>
                  <div className="orbboxbar">
                    <span className="orbboxfill" style={{ width: TIER_FILL[detail.tier] }} />
                  </div>
                  <span className="orbboxhint">
                    <span className="hint-hover">Hover a market</span>
                    <span className="hint-touch">Tap a market</span>
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div style={{ padding: '0 var(--pgut) 20px', display: 'flex', gap: 24, justifyContent: 'center' }}>
            <span className="orblegend">
              <i className="home" />
              Home base
            </span>
            <span className="orblegend">
              <i className="exp" />
              Active expansion
            </span>
            <span className="orblegend">
              <i className="road" />
              Roadmap
            </span>
          </div>

          <div
            className="elf-page__grid2"
            style={{
              padding: '0 var(--pgut) 72px',
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 16,
              borderTop: '1px solid rgba(var(--panel-fg-rgb),.1)',
            }}
          >
            <Link className="nextlink" to={route('/who-we-are')}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                <span className="statl">Same footprint</span>
                <span className="whead" style={{ fontSize: 20 }}>
                  Team &amp; Careers
                </span>
                <span className="wtag" style={{ fontSize: 12.5 }}>
                  A real, growing bench.
                </span>
              </div>
              <span className="arw">→</span>
            </Link>
            <button
              type="button"
              className="nextlink"
              onClick={() => consult?.open({ source: 'launch-markets.proposal', title: 'Request a proposal' })}
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                <span className="statl">Start here</span>
                <span className="whead" style={{ fontSize: 20 }}>
                  Request a proposal
                </span>
                <span className="wtag" style={{ fontSize: 12.5 }}>
                  Bring ELF-CIRCLE to your market.
                </span>
              </div>
              <span className="arw">→</span>
            </button>
          </div>
        </div>
      </div>
      <ElfFooter />
    </ElfShell>
  );
};

export default LaunchMarkets;
